import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Stack,
  SwipeableDrawer,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { ReactNode } from "react";

import type { MobileBottomSheetSize } from "./MobileBottomSheet";
import {
  MOBILE_SHEET_FOOTER_BUTTON_GAP,
  mobileSheetCancelButtonSx,
  mobileSheetConfirmButtonSx,
} from "./mobileSheetFooterButtons";
import {
  MOBILE_HEADER_PANEL_Z_INDEX_OFFSET,
} from "@/components/layout/private/main/components/Header/headerLayoutUtils";

export interface MobileTopSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  onConfirm?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmDisabled?: boolean;
  confirmType?: "button" | "submit";
  confirmForm?: string;
  zIndexOffset?: number;
  footer?: ReactNode;
  size?: MobileBottomSheetSize;
  topOffset?: string;
  hideHeader?: boolean;
  /** Painel do header mobile: z-index e cliques coordenados com o AppBar. */
  headerLayer?: boolean;
}

const SHEET_MAX_HEIGHT: Record<MobileBottomSheetSize, string> = {
  auto: "72dvh",
  medium: "58dvh",
  large: "85dvh",
};

const SHEET_MIN_HEIGHT: Partial<Record<MobileBottomSheetSize, string>> = {
  medium: "40dvh",
  large: "65dvh",
};

const FOOTER_SAFE_PADDING = "calc(16px + env(safe-area-inset-bottom, 0px))";

export function MobileTopSheet({
  open,
  onClose,
  title,
  subtitle,
  children,
  onConfirm,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  confirmDisabled = false,
  confirmType = "button",
  confirmForm,
  zIndexOffset = 20,
  footer,
  size = "auto",
  topOffset = "0px",
  hideHeader = false,
  headerLayer = false,
}: MobileTopSheetProps) {
  const theme = useTheme();
  const hasCustomFooter = Boolean(footer);
  const hasBuiltinFooter = Boolean(onConfirm || confirmForm);
  const showFooter = hasCustomFooter || hasBuiltinFooter;
  const maxHeight = `min(${SHEET_MAX_HEIGHT[size]}, calc(100dvh - ${topOffset} - env(safe-area-inset-top, 0px)))`;
  const resolvedZIndexOffset = headerLayer
    ? MOBILE_HEADER_PANEL_Z_INDEX_OFFSET
    : zIndexOffset;

  return (
    <SwipeableDrawer
      anchor="top"
      open={open}
      onClose={onClose}
      onOpen={() => undefined}
      disableSwipeToOpen
      swipeAreaWidth={0}
      ModalProps={{
        keepMounted: false,
        sx: {
          zIndex: theme.zIndex.modal + resolvedZIndexOffset,
          ...(headerLayer && { pointerEvents: "none" }),
        },
        BackdropProps: {
          sx: {
            bgcolor: alpha(theme.palette.common.black, 0.45),
            top: topOffset,
            ...(headerLayer && { pointerEvents: "auto" }),
          },
        },
      }}
      PaperProps={{
        sx: {
          top: topOffset,
          left: 0,
          right: 0,
          borderRadius: "0 0 20px 20px",
          maxHeight,
          minHeight: SHEET_MIN_HEIGHT[size],
          bgcolor: "background.paper",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.14)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          ...(headerLayer && { pointerEvents: "auto" }),
        },
        role: "dialog",
        "aria-label": title,
      }}
    >
      {!hideHeader ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            pt: 1.25,
            pb: 1,
            flexShrink: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          <IconButton
            size="small"
            aria-label="Fechar"
            onClick={onClose}
            sx={{ flexShrink: 0 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      ) : null}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          px: 2,
          pt: hideHeader ? 2 : 1.5,
          pb: 1.5,
        }}
      >
        {children}
      </Box>

      {showFooter ? (
        hasCustomFooter ? (
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              pt: 1,
              pb: FOOTER_SAFE_PADDING,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            {footer}
          </Box>
        ) : (
          <Stack
            direction="row"
            spacing={MOBILE_SHEET_FOOTER_BUTTON_GAP}
            sx={{
              flexShrink: 0,
              px: 2,
              pt: 1,
              pb: FOOTER_SAFE_PADDING,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              sx={mobileSheetCancelButtonSx}
            >
              {cancelLabel}
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              type={confirmType}
              form={confirmForm}
              disabled={confirmDisabled}
              onClick={confirmType === "button" ? onConfirm : undefined}
              sx={mobileSheetConfirmButtonSx}
            >
              {confirmLabel}
            </Button>
          </Stack>
        )
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pb: 1,
            pt: 0.25,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 99,
              bgcolor: "divider",
            }}
          />
        </Box>
      )}
    </SwipeableDrawer>
  );
}
